import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { updatedEntities, denormalisedEntities, denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { LISTING_STATE_DRAFT, LISTING_STATE_CLOSED, LISTING_STATE_PUBLISHED } from '../../util/types';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { boostApi } from '../../ducks/boostApi';
import { dashboardListingStats } from '../../util/api';

const RESULT_PAGE_SIZE = 42;
const SALES_QUERY_PAGE_SIZE = 100;

export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.DashboardPage;
  const resources = listingIds.map(id => ({ id, type: 'ownListing' }));
  return denormalisedEntities(ownEntities, resources, false);
};

const resultIds = data => data.data.map(l => l.id);

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  return {
    ...state,
    ownEntities: {
      ...state.ownEntities,
      ownListing: {
        ...state.ownEntities.ownListing,
        [listingEntity.id.uuid]: updatedListing,
      },
    },
  };
};

const queryOwnListingsPayloadCreator = (queryParams, { extra: sdk, dispatch, rejectWithValue }) => {
  const { perPage, ...rest } = queryParams;
  const params = { ...rest, perPage };

  return sdk.ownListings
    .query(params)
    .then(response => {
      dispatch(addOwnEntities(response));
      return response;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const queryOwnListingsThunk = createAsyncThunk(
  'app/DashboardPage/queryOwnListings',
  queryOwnListingsPayloadCreator
);

const querySalesSummaryPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  return sdk.transactions
    .query({
      only: 'sale',
      include: ['listing'],
      page: 1,
      perPage: SALES_QUERY_PAGE_SIZE,
      'fields.transaction': ['lastTransition', 'lastTransitionedAt', 'payinTotal', 'payoutTotal'],
      'fields.listing': ['title', 'state'],
    })
    .then(response => {
      const transactions = denormalisedResponseEntities(response);
      const unreadMessagesByListingId = {};
      const earningsByCurrency = {};

      transactions.forEach(tx => {
        const listingId = tx?.listing?.id?.uuid;
        const payoutTotal = tx?.attributes?.payoutTotal;
        const lastTransition = tx?.attributes?.lastTransition || '';
        const needsAttention = [
          'transition/confirm-payment',
          'transition/request-payment',
          'transition/mark-delivered',
          'transition/review-1-by-customer',
          'transition/review-2-by-customer',
          'transition/request-quote',
          'transition/make-offer',
        ].includes(lastTransition);

        if (listingId && needsAttention) {
          unreadMessagesByListingId[listingId] = (unreadMessagesByListingId[listingId] || 0) + 1;
        }

        if (payoutTotal?.currency && typeof payoutTotal.amount === 'number' && payoutTotal.amount > 0) {
          earningsByCurrency[payoutTotal.currency] =
            (earningsByCurrency[payoutTotal.currency] || 0) + payoutTotal.amount;
        }
      });

      const primaryCurrency = Object.keys(earningsByCurrency)[0] || null;
      const totalEarnings = primaryCurrency
        ? { amount: earningsByCurrency[primaryCurrency], currency: primaryCurrency }
        : null;
      const unreadMessagesCount = Object.values(unreadMessagesByListingId).reduce(
        (sum, count) => sum + count,
        0
      );

      return { unreadMessagesByListingId, unreadMessagesCount, totalEarnings };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const querySalesSummaryThunk = createAsyncThunk(
  'app/DashboardPage/querySalesSummary',
  querySalesSummaryPayloadCreator
);

const fetchListingViewStatsPayloadCreator = ({ listingIds, dateRange }, { rejectWithValue }) => {
  return dashboardListingStats({ listingIds, dateRange }).catch(e => rejectWithValue(storableError(e)));
};

export const fetchListingViewStatsThunk = createAsyncThunk(
  'app/DashboardPage/fetchListingViewStats',
  fetchListingViewStatsPayloadCreator
);

const closeListingPayloadCreator = (listingId, { extra: sdk, rejectWithValue }) => {
  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(response => response)
    .catch(e => rejectWithValue(storableError(e)));
};

export const closeListingThunk = createAsyncThunk(
  'app/DashboardPage/closeListing',
  closeListingPayloadCreator
);

const openListingPayloadCreator = (listingId, { extra: sdk, rejectWithValue }) => {
  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => response)
    .catch(e => rejectWithValue(storableError(e)));
};

export const openListingThunk = createAsyncThunk(
  'app/DashboardPage/openListing',
  openListingPayloadCreator
);

const delay = ms => new Promise(resolve => window.setTimeout(resolve, ms));

const discardDraftPayloadCreator = (listingId, thunkAPI) => {
  const { getState, extra: sdk, dispatch, rejectWithValue } = thunkAPI;
  const { queryParams } = getState().DashboardPage;

  return sdk.ownListings
    .discardDraft({ id: listingId }, { expand: true })
    .then(() => Promise.all([delay(300), sdk.ownListings.query(queryParams)]))
    .then(([_, listingResponse]) => {
      dispatch(addOwnEntities(listingResponse));
      return listingResponse;
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const discardDraftThunk = createAsyncThunk(
  'app/DashboardPage/discardDraft',
  discardDraftPayloadCreator
);

const publishDraftPayloadCreator = (listingId, { extra: sdk, rejectWithValue }) => {
  return sdk.ownListings
    .publishDraft({ id: listingId }, { expand: true })
    .then(response => response)
    .catch(e => rejectWithValue(storableError(e)));
};

export const publishDraftThunk = createAsyncThunk(
  'app/DashboardPage/publishDraft',
  publishDraftPayloadCreator
);

// Drafts hard-delete via the API; published/closed can't be deleted, so archive (flag + close).
const deleteListingPayloadCreator = async (listingId, { extra: sdk, getState, rejectWithValue }) => {
  try {
    const listing = getState().DashboardPage.ownEntities?.ownListing?.[listingId.uuid];
    const state = listing?.attributes?.state;
    if (state === LISTING_STATE_DRAFT) {
      await sdk.ownListings.discardDraft({ id: listingId });
      return { listingId };
    }
    await sdk.ownListings.update({
      id: listingId,
      publicData: { isArchived: true, archivedAt: new Date().toISOString() },
    });
    // Close a live listing so it also leaves public search; closed ones already have.
    if (state === LISTING_STATE_PUBLISHED) {
      await sdk.ownListings.close({ id: listingId });
    }
    return { listingId };
  } catch (e) {
    return rejectWithValue(storableError(e));
  }
};

export const deleteListingThunk = createAsyncThunk(
  'app/DashboardPage/deleteListing',
  deleteListingPayloadCreator
);

const dashboardPageSlice = createSlice({
  name: 'DashboardPage',
  initialState: {
    pagination: null,
    queryParams: null,
    queryInProgress: false,
    queryListingsError: null,
    currentPageResultIds: [],
    ownEntities: {},
    openingListing: null,
    openingListingError: null,
    closingListing: null,
    closingListingError: null,
    discardingDraft: null,
    discardingDraftError: null,
    publishingListing: null,
    publishingListingError: null,
    deletingListing: null,
    deletingListingError: null,
    unreadMessagesByListingId: {},
    unreadMessagesCount: 0,
    totalEarnings: null,
    viewStatsByListingId: {},
    totalViews: 0,
    viewsFetchInProgress: false,
    viewsFetchError: null,
    viewsDateRange: '30d',
  },
  reducers: {
    clearOpenListingError: state => {
      state.openingListing = null;
      state.openingListingError = null;
    },
    addOwnEntities: (state, action) => {
      state.ownEntities = updatedEntities({ ...state.ownEntities }, action.payload.data);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(queryOwnListingsThunk.pending, (state, action) => {
        state.queryParams = action.meta.arg;
        state.queryInProgress = true;
        state.queryListingsError = null;
        state.currentPageResultIds = [];
      })
      .addCase(queryOwnListingsThunk.fulfilled, (state, action) => {
        state.currentPageResultIds = resultIds(action.payload.data);
        state.pagination = action.payload.data.meta;
        state.queryInProgress = false;
      })
      .addCase(queryOwnListingsThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.queryInProgress = false;
        state.queryListingsError = action.payload;
      })
      .addCase(querySalesSummaryThunk.fulfilled, (state, action) => {
        state.unreadMessagesByListingId = action.payload.unreadMessagesByListingId;
        state.unreadMessagesCount = action.payload.unreadMessagesCount;
        state.totalEarnings = action.payload.totalEarnings;
      })
      .addCase(fetchListingViewStatsThunk.pending, state => {
        state.viewsFetchInProgress = true;
        state.viewsFetchError = null;
      })
      .addCase(fetchListingViewStatsThunk.fulfilled, (state, action) => {
        state.viewsFetchInProgress = false;
        state.viewStatsByListingId = action.payload.viewsByListingId || {};
        state.totalViews = action.payload.totalViews || 0;
        state.viewsDateRange = action.payload.dateRange || '30d';
      })
      .addCase(fetchListingViewStatsThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.viewsFetchInProgress = false;
        state.viewsFetchError = action.payload;
      })
      .addCase(openListingThunk.pending, (state, action) => {
        state.openingListing = action.meta.arg;
        state.openingListingError = null;
      })
      .addCase(openListingThunk.fulfilled, (state, action) => {
        const updatedState = updateListingAttributes(state, action.payload.data.data);
        state.ownEntities = updatedState.ownEntities;
        state.openingListing = null;
      })
      .addCase(openListingThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.openingListingError = { listingId: state.openingListing, error: action.payload };
        state.openingListing = null;
      })
      .addCase(closeListingThunk.pending, (state, action) => {
        state.closingListing = action.meta.arg;
        state.closingListingError = null;
      })
      .addCase(closeListingThunk.fulfilled, (state, action) => {
        const updatedState = updateListingAttributes(state, action.payload.data.data);
        state.ownEntities = updatedState.ownEntities;
        state.closingListing = null;
      })
      .addCase(closeListingThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.closingListingError = { listingId: state.closingListing, error: action.payload };
        state.closingListing = null;
      })
      .addCase(discardDraftThunk.pending, (state, action) => {
        state.discardingDraft = action.meta.arg;
        state.discardingDraftError = null;
      })
      .addCase(discardDraftThunk.fulfilled, (state, action) => {
        state.currentPageResultIds = resultIds(action.payload.data);
        state.pagination = action.payload.data.meta;
        state.discardingDraft = null;
      })
      .addCase(discardDraftThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.discardingDraftError = { listingId: state.discardingDraft, error: action.payload };
        state.discardingDraft = null;
      })
      .addCase(publishDraftThunk.pending, (state, action) => {
        state.publishingListing = action.meta.arg;
        state.publishingListingError = null;
      })
      .addCase(publishDraftThunk.fulfilled, (state, action) => {
        const updatedState = updateListingAttributes(state, action.payload.data.data);
        state.ownEntities = updatedState.ownEntities;
        state.publishingListing = null;
      })
      .addCase(publishDraftThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.publishingListingError = { listingId: state.publishingListing, error: action.payload };
        state.publishingListing = null;
      })
      .addCase(deleteListingThunk.pending, (state, action) => {
        state.deletingListing = action.meta.arg;
        state.deletingListingError = null;
      })
      .addCase(deleteListingThunk.fulfilled, (state, action) => {
        const id = action.payload.listingId;
        state.currentPageResultIds = state.currentPageResultIds.filter(rid => rid.uuid !== id.uuid);
        state.deletingListing = null;
      })
      .addCase(deleteListingThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.deletingListingError = { listingId: state.deletingListing, error: action.payload };
        state.deletingListing = null;
      });
  },
});

export const { clearOpenListingError, addOwnEntities } = dashboardPageSlice.actions;
export default dashboardPageSlice.reducer;

export const closeListing = listingId => dispatch => dispatch(closeListingThunk(listingId)).unwrap();
export const openListing = listingId => dispatch => dispatch(openListingThunk(listingId)).unwrap();
export const discardDraft = listingId => dispatch => dispatch(discardDraftThunk(listingId)).unwrap();
export const deleteListing = listingId => dispatch => dispatch(deleteListingThunk(listingId)).unwrap();
export const publishListingDraft = listingId => dispatch => dispatch(publishDraftThunk(listingId));
export const fetchListingViewStats = (listingIds, dateRange = '30d') => dispatch =>
  dispatch(fetchListingViewStatsThunk({ listingIds, dateRange }));

export const loadData = (params, search, config) => async dispatch => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;
  dispatch(clearOpenListingError());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const [_, listingsResult] = await Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(
      queryOwnListingsThunk({
        ...queryParams,
        page,
        perPage: RESULT_PAGE_SIZE,
        pub_isArchived: false,
        include: ['images', 'currentStock'],
        'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        'limit.images': 1,
      })
    ),
    dispatch(querySalesSummaryThunk()),
  ]);

  if (queryOwnListingsThunk.fulfilled.match(listingsResult)) {
    const listingIds = resultIds(listingsResult.payload.data).map(id => id.uuid);
    if (listingIds.length > 0) {
      await Promise.all([
        dispatch(fetchListingViewStatsThunk({ listingIds, dateRange: '30d' })),
        dispatch(
          boostApi.endpoints.fetchBoosts.initiate({ page: 1, limit: RESULT_PAGE_SIZE })
        ).catch(() => {}),
      ]);
    }
  }
};

export const getListingFilterType = listing => {
  const state = listing?.attributes?.state;
  if (state === LISTING_STATE_DRAFT) {
    return 'drafts';
  }
  if (state === LISTING_STATE_CLOSED) {
    return 'closed';
  }
  return 'published';
};
